import {BasePopup} from "components/popup/BasePopup"
import styles from "./BatchDailyAttendanceCheckin.module.css"
import {BaseButton, BaseLoading, KlassDropdown} from "components"
import {Icon} from "components/Icon"
import {BaseTimePicker} from "components/DateTimePicker"
import moment, {Moment} from "moment"
import {useModel, useMutation} from "hooks"
import {useCallback, useEffect, useMemo, useRef, useState, Fragment} from "react"
import cx from "classnames"
import {Checkbox} from "antd"
import {get, uniqBy} from "lodash"
import {getFullName, handleError, toastError, toastInfo, toastSuccess, getFieldLabel} from "helpers"
import {attendanceService} from "services"
import {AttendanceEvent, AttendanceEventType} from "types/attendance"
import {useAttendanceCheckBreakEventSetting} from "hooks/useAttendanceCheckBreakEventSetting"
import {Settings} from "types/settings"
import {KlassappTable} from "uiKit"
import {KlassappTableHOC} from "HOC"
import {KlassappTableProps} from "types/common"
import {useQuery} from "@tanstack/react-query"
import {UNLIMITED_PAGE_SIZE} from "data/constants"

const EVENT_OPTIONS: Array<{name: string; value: AttendanceEventType}> = [
    {name: "Checkin", value: AttendanceEventType.Checkin},
    {name: "Checkout", value: AttendanceEventType.Checkout},
    {name: "In", value: AttendanceEventType.BreakEnd},
    {name: "Out", value: AttendanceEventType.BreakStart}
]

type AttendanceItem = {
    attendanceId: number
    events: AttendanceEvent[]
}

type BatchRowResult = {
    status: "success" | "skipped" | "failed"
    message: string
}

type Props = {
    isShow: boolean
    date: string
    filterParams: Record<string, any>
    onClose: () => void
    onSuccess: () => void
} & KlassappTableProps

function BatchDailyAttendanceCheckinTable(props: Props) {
    const {
        isShow,
        onClose,
        date,
        filterParams,
        onSuccess,
        data,
        columns,
        fields,
        allFields,
        isLoading,
        isLoadedTableFuncs,
        orderField,
        dispatch,
        getCurrentData
    } = props
    const model = useModel()
    const dateFormat = model.getUserDateFormat()
    const timeFormat = model.getUserTimeFormat()
    const {isBreakEnabled} = useAttendanceCheckBreakEventSetting()

    const [selectedTime, setSelectedTime] = useState<Moment | null>(moment())
    const [selectedEventType, setSelectedEventType] = useState<{name: string; value: AttendanceEventType} | null>(null)
    const [isMakeup, setIsMakeup] = useState(false)
    const [distEdEnabled, setDistEdEnabled] = useState(false)
    const wasOpenRef = useRef(false)
    const batchResultsRef = useRef<Record<number, BatchRowResult>>({})
    const selectedMapRef = useRef<Record<number, true>>({})
    const selectedCount = getCurrentData()?.filter((item) => item.isChecked).length ?? 0

    const eventTypeOptions = useMemo(() => {
        if (isBreakEnabled) {
            return EVENT_OPTIONS
        }
        return EVENT_OPTIONS.filter(
            (item) => ![AttendanceEventType.BreakStart, AttendanceEventType.BreakEnd].includes(item.value)
        )
    }, [isBreakEnabled])

    const renderBatchResult = useCallback((batchResult?: BatchRowResult | null) => {
        if (!batchResult) {
            return <span className={styles.resultEmpty}>—</span>
        }
        return (
            <span
                className={cx(styles.resultBadge, {
                    [styles.resultSuccess]: batchResult.status === "success",
                    [styles.resultSkipped]: batchResult.status === "skipped",
                    [styles.resultFailed]: batchResult.status === "failed"
                })}
                title={batchResult.message}>
                {batchResult.message}
            </span>
        )
    }, [])

    const normalizeItem = useCallback(
        (daily: any) => {
            const student = daily?.student ?? {}
            const shifts: any[] = uniqBy(student.shifts ?? [], "shiftId")
            const phases: any[] = uniqBy(student.phases ?? [], "phaseId")
            const profileId = student.id ?? daily.profileId
            const batchResult = batchResultsRef.current[profileId] ?? null

            return {
                ...daily,
                id: profileId,
                profileId,
                customProfileId: student?.customProfileId ?? "",
                customUserId: student.customUserId ?? "",
                userId: student.userId,
                firstName: student.firstName ?? "",
                lastName: student.lastName ?? "",
                studentName: getFullName(student),
                phaseNames: phases.map((phase) => phase.name).join(",") ?? "",
                shiftNames: shifts.map((shift) => shift.name).join(",") ?? "",
                majorVersionHtml: student.currentMajorVersionId?.versionName
                    ? `${student.currentMajorVersionId.versionName} (${student.currentMajorVersionId.versionCode})`
                    : "",
                majorHtml: student.currentMajor?.name
                    ? `${student.currentMajor.name} (${student.currentMajor.code})`
                    : "",
                isChecked: !!selectedMapRef.current[profileId],
                batchResult,
                batchResultHtml: renderBatchResult(batchResult)
            }
        },
        [renderBatchResult]
    )

    const listQuery = useQuery(
        ["batch-daily-attendance-checkin", isShow, date, filterParams, orderField?.field, orderField?.order],
        async () => {
            const {data: rows} = await attendanceService.getListRollCall({
                range: {
                    page: 1,
                    pageSize: UNLIMITED_PAGE_SIZE
                },
                sort: orderField?.field
                    ? {
                          orderBy: orderField.field,
                          orderDir: orderField.order
                      }
                    : undefined,
                ...filterParams
            })
            return (rows || []).map((item) => normalizeItem(item))
        },
        {
            enabled: isShow && !!isLoadedTableFuncs,
            onSuccess: (students) => {
                dispatch({data: students, total: students.length, isLoading: false})
                if (wasOpenRef.current && students.length <= 0) {
                    toastInfo("There are no students to update")
                    onClose()
                }
            },
            onError: (err) => {
                handleError(err)
                dispatch({data: [], total: 0, isLoading: false})
            }
        }
    )

    useEffect(() => {
        dispatch({isClassComponent: false})
        props.dispatchFunc([
            {key: "getListData", func: listQuery.refetch},
            {key: "getFields", func: getFields},
            {key: "getColumns", func: getColumns}
        ])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (!isShow) {
            wasOpenRef.current = false
            batchResultsRef.current = {}
            selectedMapRef.current = {}
            setSelectedTime(moment())
            setSelectedEventType(null)
            setIsMakeup(false)
            setDistEdEnabled(false)
            dispatch({data: [], total: 0})
            return
        }

        if (!wasOpenRef.current) {
            wasOpenRef.current = true
            setSelectedTime(moment())
            setSelectedEventType(null)
            setIsMakeup(false)
            setDistEdEnabled(false)
            batchResultsRef.current = {}
            selectedMapRef.current = {}
        }
    }, [isShow, dispatch])

    function getColumns() {
        return [
            {
                title: getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
                field: "customProfileId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "customProfileId"
            },
            {
                title: "Custom User Id",
                field: "customUserId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "customUserId"
            },
            {
                title: "User Id",
                field: "userId",
                headerStyle: {minWidth: "100px"},
                sortable: true,
                orderField: "userId"
            },
            {
                title: "First Name",
                field: "firstName",
                headerStyle: {minWidth: "120px"},
                sortable: true,
                orderField: "firstName"
            },
            {
                title: "Last Name",
                field: "lastName",
                headerStyle: {minWidth: "120px"},
                sortable: true,
                orderField: "lastName"
            },
            {
                title: "Phase",
                field: "phaseNames",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Shift",
                field: "shiftNames",
                headerStyle: {minWidth: "100px"}
            },
            {
                title: "Major",
                field: "majorHtml",
                headerStyle: {minWidth: "120px"}
            },
            {
                title: "Major Version",
                field: "majorVersionHtml",
                headerStyle: {minWidth: "120px"}
            },
            {
                title: "Result",
                field: "batchResultHtml",
                headerStyle: {minWidth: "140px"}
            }
        ]
    }

    function getFields() {
        return [
            getFieldLabel(model, Settings.GeneralLabel.EnrollmentId, "Enrollment Id"),
            "Custom User Id",
            "User Id",
            "First Name",
            "Last Name",
            "Phase",
            "Shift",
            "Major",
            "Major Version",
            "Result"
        ]
    }

    const onUpdateRowData = (updatedData) => {
        const profileId = updatedData.profileId ?? updatedData.id
        if (updatedData.isChecked) {
            selectedMapRef.current[profileId] = true
        } else {
            delete selectedMapRef.current[profileId]
        }
        props.onUpdateRowData(updatedData)
    }

    const onUpdateTableData = (newData) => {
        const nextSelected: Record<number, true> = {...selectedMapRef.current}
        newData.forEach((item) => {
            const profileId = item.profileId ?? item.id
            if (item.isChecked) {
                nextSelected[profileId] = true
            } else {
                delete nextSelected[profileId]
            }
        })
        selectedMapRef.current = nextSelected
        props.onUpdateTableData(newData)
    }

    const onSubmit = () => {
        if (!selectedTime) {
            toastError("Please select a time")
            return
        }

        if (!selectedEventType?.value) {
            toastError("Please select an event type")
            return
        }

        const selectedStudents = (getCurrentData() || []).filter((item) => item.isChecked)
        if (!selectedStudents.length) {
            toastError("Please select at least one student")
            return
        }

        const filterDate = moment(date)
        const baseTime = moment(selectedTime).set({
            date: filterDate.date(),
            month: filterDate.month(),
            year: filterDate.year(),
            seconds: 0,
            milliseconds: 0
        })

        const payloadStudents = selectedStudents
            .map((item) => {
                const campusId = get(item, ["student", "campuses", 0, "id"])
                const majorVersionId = get(item, ["student", "currentMajorVersionId", "id"])
                const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()
                const profileId = item.profileId ?? item.student?.id

                if (!campusId || !profileId) {
                    return null
                }

                return {
                    profileId,
                    campusId,
                    majorVersionId,
                    eventType: selectedEventType.value,
                    timezone,
                    isMakeup,
                    distEdEnabled,
                    createdAt: moment(baseTime).tz(timezone, true).toISOString()
                }
            })
            .filter(Boolean)

        if (!payloadStudents.length) {
            toastError("Selected students are missing campus information")
            return
        }

        batchCreateMutation.mutate({
            createdAt: baseTime.toISOString(),
            message: "Batch check-in/out from roll call",
            students: payloadStudents as any
        })
    }

    const batchCreateMutation = useMutation(attendanceService.batchCreateDailyAttendanceEvents, {
        onSuccess: (response) => {
            const responseData = response?.data ?? response
            const successCount = responseData?.successCount ?? 0
            const failedCount = responseData?.failedCount ?? 0
            const skippedCount = responseData?.skippedCount ?? 0
            const results = responseData?.results ?? []

            const nextResults: Record<number, BatchRowResult> = {...batchResultsRef.current}
            results.forEach((result: any) => {
                if (!result?.profileId) return
                if (result.skipped) {
                    nextResults[result.profileId] = {
                        status: "skipped",
                        message: result.skipReason || "Skipped"
                    }
                } else if (!result.success) {
                    nextResults[result.profileId] = {
                        status: "failed",
                        message: result.error || "Failed"
                    }
                } else {
                    nextResults[result.profileId] = {
                        status: "success",
                        message: "Updated"
                    }
                }
            })
            batchResultsRef.current = nextResults

            if (failedCount > 0 && (successCount > 0 || skippedCount > 0)) {
                toastInfo(`Updated ${successCount}. Skipped ${skippedCount}. Failed ${failedCount}. See Result column.`)
            } else if (failedCount > 0) {
                toastError(`Failed ${failedCount} student(s). See Result column.`)
            } else if (skippedCount > 0 && successCount === 0) {
                toastInfo(`Skipped ${skippedCount} student(s). See Result column.`)
            } else if (skippedCount > 0) {
                toastInfo(`Updated ${successCount}. Skipped ${skippedCount}. See Result column.`)
            } else {
                toastSuccess("Batch check-in/out successfully")
            }
            onSuccess()
            listQuery.refetch()
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const renderAttendanceEventsPanel = useCallback(
        (item: any) => {
            const attendances: AttendanceItem[] = item.attendances ?? []
            if (!attendances.length) {
                return null
            }

            const timezone = get(item, ["student", "timezone"]) ?? moment.tz.guess()

            return (
                <div className={styles.expandedAttendancePanel}>
                    <div className={styles.eventsContainer}>
                        {attendances.map((attendance, attendanceIndex) => (
                            <Fragment key={attendance.attendanceId ?? attendanceIndex}>
                                {attendance.events?.map((event, eventIndex) => (
                                    <div
                                        key={event.id ?? `${attendance.attendanceId ?? attendanceIndex}-${eventIndex}`}
                                        className={styles.eventItem}>
                                        <div className={styles.event}>
                                            {event.eventType === AttendanceEventType.Checkin ? (
                                                <Icon
                                                    icon="CHECKIN_FILL"
                                                    className={styles.eventIcon}
                                                    color="#18a957"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.Checkout ? (
                                                <Icon
                                                    icon="CHECKOUT_FILL"
                                                    className={styles.eventIcon}
                                                    color="#DF1642"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakEnd ? (
                                                <Icon
                                                    icon="ATTENDANCE_IN"
                                                    className={styles.eventIcon}
                                                    color="#18a957"
                                                />
                                            ) : null}
                                            {event.eventType === AttendanceEventType.BreakStart ? (
                                                <Icon
                                                    icon="ATTENDANCE_OUT"
                                                    className={styles.eventIcon}
                                                    color="#DF1642"
                                                />
                                            ) : null}
                                            <span className={styles.eventTime}>
                                                {moment(event.createdAt).tz(timezone).format(timeFormat)}
                                            </span>
                                        </div>
                                        {eventIndex !== attendance.events?.length - 1 ? (
                                            <div className={styles.eventSeparator} />
                                        ) : null}
                                    </div>
                                ))}
                                {attendanceIndex !== attendances.length - 1 ? (
                                    <div className={styles.eventSeparator} />
                                ) : null}
                            </Fragment>
                        ))}
                    </div>
                </div>
            )
        },
        [timeFormat]
    )

    const expandable = useMemo(
        () => ({
            isExpanded: true,
            rowExpandable: (record) => !!(record.attendances ?? []).length,
            expandedRowRender: renderAttendanceEventsPanel
        }),
        [renderAttendanceEventsPanel]
    )

    return (
        <BasePopup isShow={isShow} onClose={onClose} width="90vw" leftIcon="PERSON_ADD" destroyOnClose keyboard={false}>
            <div className={styles.container}>
                <div className={styles.formTitle}>Batch Check-In / Check-Out on {moment(date).format(dateFormat)}</div>
                <div className={styles.timeRow}>
                    <span className={styles.timeLabel}>
                        Time<span className={styles.star}>*</span>
                    </span>
                    <BaseTimePicker
                        style={{width: 160}}
                        value={selectedTime}
                        format={timeFormat}
                        allowClear={false}
                        onChange={(value) => setSelectedTime(value)}
                        placeholder="Time"
                    />
                    <span className={styles.timeLabel}>
                        Event Type<span className={styles.star}>*</span>
                    </span>
                    <div className={styles.eventTypeSelect}>
                        <KlassDropdown
                            options={eventTypeOptions}
                            value={selectedEventType}
                            valueKey="value"
                            labelKey="name"
                            placeholder="Select event type"
                            isClearable={false}
                            onChange={(option) => setSelectedEventType(option)}
                        />
                    </div>
                    <label className={styles.flagCheckbox}>
                        <Checkbox checked={isMakeup} onChange={(e) => setIsMakeup(e.target.checked)} />
                        <span>Is Makeup</span>
                    </label>
                    <label className={styles.flagCheckbox}>
                        <Checkbox checked={distEdEnabled} onChange={(e) => setDistEdEnabled(e.target.checked)} />
                        <span>DistEd</span>
                    </label>
                </div>
                <div className={styles.tableContainer}>
                    <KlassappTable
                        columns={columns}
                        data={data}
                        isLoading={isLoading || listQuery.isFetching}
                        fields={fields}
                        allFields={allFields}
                        orderField={orderField}
                        isShowCheckedColumn
                        isShowCheckedColumnHeader
                        isShowScrollTable
                        onClickSortColumn={props.onClickSortColumn}
                        onChangeFields={props.onChangeFields}
                        onUpdateRowData={onUpdateRowData}
                        onUpdateTableData={onUpdateTableData}
                        onChangeAllFields={props.onChangeAllFields}
                        expandable={expandable}
                    />
                </div>
                <div className={styles.actionWrap}>
                    <BaseButton
                        title="Cancel"
                        onClick={onClose}
                        variant="secondary"
                        loading={batchCreateMutation.isLoading}
                    />
                    <BaseButton
                        title={`Submit (${selectedCount})`}
                        onClick={onSubmit}
                        disabled={selectedCount <= 0}
                        loading={batchCreateMutation.isLoading}
                    />
                </div>
            </div>
            <BaseLoading isShow={batchCreateMutation.isLoading} />
        </BasePopup>
    )
}

export const BatchDailyAttendanceCheckin = KlassappTableHOC(BatchDailyAttendanceCheckinTable)
